import 'core-js/modules/es.array.iterator.js';
import 'core-js/modules/web.dom-collections.iterator.js';
import 'core-js/modules/es.object.assign.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { styled, useIsMobile, Box } from '@hub-la/design-system';
import { useContainer } from '@hub-la/fe-container';
import { Analytics } from '@hub-la/fe-core-analytics';
import { debounce } from 'lodash';
import { useState, useRef, useCallback, useEffect } from 'react';
import { Events } from '../../../domain/dtos/events.js';
import { GetAssetPlaybackId } from '../../../usecases/get-asset-playbackid.js';
import { useRealtimeSections } from '../../hooks/use-realtime-sections.js';
import '@hub-la/fe-core-http-client';
import 'react-query';
import '../../../usecases/save-file.js';
import '../../../domain/dtos/query-key.js';
import '../../../usecases/save-post.js';
import { useUserProgress } from '../../hooks/use-user-progress.js';
import UpNextCard from '../upnext-card/index.js';
import MuxPlayer from './mux-player.js';

let _ = t => t,
  _t;
const MuxPlayerRewrite = styled(MuxPlayer)(_t || (_t = _`
  height: 100%;
  width: 100%;

  --seek-backward-button: none;
  --seek-forward-button: none;
  --media-accent-color: none !important;
  media-preview-time-display {
    color: #fff;
  }

  &::part(poster-layer) {
    display: none;
  }

  &::part(control-bar bottom) {
    display: var(--media-control-display, var(--media-control-bar-display, inline-flex));
  }
  &::part(bottom time range) ::slotted(media-preview-thumbnail) {
    background: red;
  }
  &::part(bottom time range) {
    display: inline-block;
    --media-range-bar-color: #fff;
    --media-time-range-buffered-color: #ffffff4d;
    --media-range-track-background: #ffffff33;
    --media-range-track-height: 1px;
    --media-preview-border-radius: 8px;
    --media-preview-thumbnail-border-radius: 8px;
    --media-preview-thumbnail-border: 1px solid #fff;
    --media-preview-chapter-background: transparent;
    --media-preview-time-background: transparent;
    --media-box-arrow-display: none;
    --media-text-color: #fff;
  }
  media-playback-rate-listbox[part~='listbox']::part(option-selected) {
    color: black;
  }
`));
const Player = ({
  src,
  assetId,
  progress,
  postId,
  sectionId
}) => {
  const container = useContainer();
  const analytics = container.get(Analytics);
  const isMobile = useIsMobile();
  const [videoIsEnded, setVideoIsEnded] = useState(false);
  const muxPlayerRef = useRef(null);
  const videoRef = muxPlayerRef === null || muxPlayerRef === void 0 ? void 0 : muxPlayerRef.current;
  const {
    setRealtimePostProgress
  } = useRealtimeSections();
  const {
    updateUserProgress
  } = useUserProgress({
    assetId,
    sectionId,
    postId,
    progress
  });
  const handleVideoChange = () => {
    setVideoIsEnded(false);
  };
  const handleVideoEnd = () => {
    setVideoIsEnded(true);
  };
  const addEvents = useCallback(player => {
    const handlePlay = () => {
      updateUserProgress(player.currentTime, player.duration);
      if (player.currentTime === 0) {
        analytics.track(Events.HUB.PROGRESS.STARTED, {
          assetId,
          type: 'video',
          userProgress: progress !== null && progress !== void 0 ? progress : 0,
          current: player.currentTime,
          total: player.duration
        });
      }
    };
    const handlePause = () => {
      updateUserProgress(player.currentTime, player.duration);
    };
    const handleError = error => {
      console.warn(error);
      analytics.track(Events.HUB.PROGRESS.REPRODUCTION_ERROR, {
        assetId,
        type: 'video',
        userProgress: progress !== null && progress !== void 0 ? progress : 0,
        current: player.currentTime,
        total: player.duration
      });
    };
    const handleEnded = () => {
      analytics.track(Events.HUB.PROGRESS.ENDED, {
        assetId,
        type: 'video',
        userProgress: progress !== null && progress !== void 0 ? progress : 0,
        current: player.currentTime,
        total: player.duration
      });
    };
    player.addEventListener('play', handlePlay);
    player.addEventListener('pause', handlePause);
    player.addEventListener('error', handleError);
    player.addEventListener('ended', handleEnded);
    return () => {
      player.removeEventListener('play', handlePlay);
      player.removeEventListener('pause', handlePause);
      player.removeEventListener('error', handleError);
      player.removeEventListener('ended', handleEnded);
    };
  }, [progress, updateUserProgress, src]);
  // Disable download button from video controls and right click
  const removeDownloadButton = () => {
    var _a;
    if (!videoRef) return;
    const videoEL = (_a = videoRef.media) === null || _a === void 0 ? void 0 : _a.nativeEl;
    if (!videoEL) return;
    videoEL.setAttribute('controlsList', 'nodownload');
  };
  // Video events to save user progress and analytics
  useEffect(() => {
    if (!videoRef) return;
    removeDownloadButton();
    setRealtimePostProgress(progress !== null && progress !== void 0 ? progress : 0);
    const removeEvents = addEvents(videoRef);
    return () => {
      removeEvents();
    };
  }, [videoRef]);
  // Debounce to not re-update same progress time
  const updateRealtimeProgressDebounce = useCallback(debounce(currentTime => {
    setRealtimePostProgress(Math.trunc(currentTime || 0));
  }, 1000), []);
  // Update section-content UI progress in realtime
  useEffect(() => {
    const runTimeUpdate = () => {
      const currentTime = Math.trunc((videoRef === null || videoRef === void 0 ? void 0 : videoRef.currentTime) || 0);
      // Update progress every 3 seconds of video time to avoid too many updates
      if (progress === currentTime || currentTime <= 0 || currentTime % 3 !== 0) return;
      // Dont update UI progress if video is in fullscreen to optimize performance
      const isFullScreen = Boolean(document.fullscreenElement);
      if (isFullScreen) return;
      updateRealtimeProgressDebounce(currentTime);
    };
    videoRef === null || videoRef === void 0 ? void 0 : videoRef.addEventListener('timeupdate', runTimeUpdate);
    return () => {
      videoRef === null || videoRef === void 0 ? void 0 : videoRef.removeEventListener('timeupdate', runTimeUpdate);
    };
  }, [setRealtimePostProgress, updateRealtimeProgressDebounce, videoRef]);
  // When umount player save user progress
  useEffect(() => {
    const player = videoRef;
    return () => {
      if (!player || !(player === null || player === void 0 ? void 0 : player.duration)) return;
      // When user progress is same as video time, do not update user progress
      if (player.currentTime === progress) return;
      updateUserProgress(player.currentTime, player.duration);
    };
  }, [videoRef, videoRef === null || videoRef === void 0 ? void 0 : videoRef.duration]);
  // Get Mux playbackId from src to use preview-thumbnail
  const playbackId = new GetAssetPlaybackId().execute(src);
  return jsxs(Box, Object.assign({
    position: "relative",
    height: "100%",
    width: "100%",
    onContextMenu: e => {
      // prevent right click on video to avoid download
      e.preventDefault();
    }
  }, {
    children: [!isMobile && videoIsEnded && jsx(UpNextCard, {
      postId: postId
    }), jsx(MuxPlayerRewrite, {
      ref: muxPlayerRef,
      src: src,
      playbackId: playbackId,
      onPlay: handleVideoChange,
      onSeeked: handleVideoChange,
      onEnded: handleVideoEnd,
      startTime: progress
    })]
  }));
};

export { Player };
